import { DEFAULT_DIMENSION_WEIGHTS } from '#features/KOLInquiryForQuotation/WeightSettingForm';
import dayjs from 'dayjs';
import _ from 'lodash';

export enum QuotationsType {
  YIKOUJIA = 'buyout_price',
  XIAOHAO = 'by_consume',
}

export const COMMON_FIELDS = [
  {
    custom_field_id: 1,
    field_format: 'date',
    identify: 'begin_at',
    is_required: true,
    name: '开始时间',
  },
  {
    custom_field_id: 2,
    field_format: 'date',
    identify: 'end_at',
    is_required: true,
    name: '结束时间',
  },
  {
    custom_field_id: 49,
    field_format: 'text',
    identify: 'demand_detail_default',
    submit_key: 'description',
    is_required: true,
    name: '需求详情',
    possible_values: '避免退单，请务必准确填写',
  },
];

// 需求单模型转换
const mergeKeys = ['account', 'superior_resource_ships'];
// 对齐需求合作内容
function alignCooperate_accounts(cooperate_accounts) {
  return (
    cooperate_accounts?.map((item) => {
      const property_values = item?.property_values || [];
      mergeKeys.forEach((key, index) => {
        switch (key) {
          case 'account':
            property_values.push({
              index_id: index,
              property_schema: {
                property_id: 10000,
                required: false,
                property_name: '需求合作内容',
                property_code: 'account',
                property_type: 'input',
              },
              property_code: 'account',
              value: [item[key]],
            }); // 如果没有account属性，就添加一个
            return;
          case 'superior_resource_ships':
            property_values.push({
              index_id: index,
              property_schema: {
                property_id: 10000 + index,
                required: false,
                property_code: 'account' + index,
                property_name: '合作平台',
                property_type: 'dropdown_list',
                property_options: [],
              },
              property_code: 'account' + index,
              value:
                item?.superior_resource_ships?.map((item) => item.name) || [],
            });
            return;
          default:
            property_values.push({
              index_id: index,
              property_schema: {
                property_id: 10000,
                required: false,
                property_name: '其他' + index,
                property_code: 'account' + index,
                property_type: 'input',
              },
              property_code: 'account' + index,
              value: [item[key]],
            }); // 其他属性
            return;
        }
      });
      return {
        ...item,
        property_values,
      };
    }) || []
  );
}

// 对齐结构化模型
function alignAtStructured(demandDetail) {
  if (
    !demandDetail.structured &&
    demandDetail.depth > 0 && // 二级单级以上
    !['PENDING_DESIGN'].includes(demandDetail.state.toUpperCase()) && // 非待设计状态
    // && ['CHANNEL_GROUP'].includes(demandDetail.group_type.toUpperCase()) // 渠道中心
    demandDetail.quote_score_enable // 支持询比价评分
  ) {
    demandDetail.$showStructured = true;
    if (demandDetail?.demand_plan_info?.cooperate_accounts) {
      demandDetail.demand_plan_info.cooperate_accounts =
        alignCooperate_accounts(
          demandDetail.demand_plan_info.cooperate_accounts
        );
    }
  }
}

/**
 * 详情页 - 详情数据结构的转换,模型转换，$$detail
 * 用来转换新流程详情数据结构中以“demand_”开头的字段为"project_"开头的字段
 * 原因：原社媒流程详情接口的'project_**'字段在新流程详情数据结构中被后端改成了'demand_**'字段
 *      因此为了兼容新流程复用的社媒流程组件的正常使用，特此定义了该方法进行数据结构的转换
 * @param demandDetail
 */
export const convertDemandDetailToProjectDetail = (demandDetail: any) => {
  const {
    child_demands,
    demand_plan_info,
    demand_principals,
    demand_suppliers,
    translated_purchasing_method,
    translated_purchasing_status,
    is_bind_execution_data,
    bidding_id,
  } = demandDetail;

  demandDetail.child_projects = child_demands;
  if (demandDetail.child_projects) {
    demandDetail.child_projects.forEach((cp: any) => {
      const { demand_plan_info: childPlanInfo } = cp;

      if (childPlanInfo) {
        childPlanInfo.project_id = childPlanInfo.demand_id;
      }

      cp.project_plan_info = childPlanInfo;
    });
  }

  demandDetail.project_plan_info = demand_plan_info;
  if (demandDetail.project_plan_info) {
    const { demand_id, demand_planners } = demandDetail.project_plan_info;

    demandDetail.project_plan_info.project_id = demand_id;
    demandDetail.project_plan_info.custom_values = [];
    demandDetail.project_plan_info.translated_purchasing_method =
      translated_purchasing_method;
    demandDetail.project_plan_info.translated_purchasing_status =
      translated_purchasing_status;
    demandDetail.project_plan_info.bidding_id = bidding_id;
    demandDetail.project_plan_info.is_bind_execution_data =
      is_bind_execution_data;
    demandDetail.project_plan_info.kpi_type = demandDetail.kpi_type;
    if (demandDetail.project_plan_info?.begin_at && demandDetail.project_plan_info?.end_at) {
      demandDetail.project_plan_info.$begin_and_end_at = [demandDetail.project_plan_info?.begin_at, demandDetail.project_plan_info?.end_at];
    }

    COMMON_FIELDS.forEach((cf: any) => {
      const customValue = {
        custom_field: { ...cf },
        custom_field_id: cf.custom_field_id,
        id: cf.custom_field_id,
        value: demandDetail.project_plan_info[cf.submit_key || cf.identify],
      };

      demandDetail.project_plan_info.custom_values.push(customValue);
    });

    demandDetail.project_plan_info.project_planners = demand_planners;
    
    let preFillQuoteData = demandDetail?.project_plan_info?.prefill_quote_data;
    if (preFillQuoteData && typeof preFillQuoteData === 'string') {
      try {
        preFillQuoteData = JSON.parse(preFillQuoteData);
      } catch (error) {
        preFillQuoteData = null;
      }
    }
    demandDetail.project_plan_info.prefill_quote_data = preFillQuoteData;
  }

  demandDetail.project_principals = demand_principals;
  if (demandDetail.project_principals) {
    demandDetail.project_principals.forEach((pp: any) => {
      const { demand_id } = pp;

      pp.project_id = demand_id;
    });
  }

  demandDetail.project_suppliers = demand_suppliers;
  if (demandDetail.project_suppliers) {
    demandDetail.project_suppliers.forEach((ps: any) => {
      const { demand_id, demand_supplier_quotes } = ps;

      ps.project_id = demand_id;

      if (demand_supplier_quotes) {
        ps.project_supplier_quotes = demand_supplier_quotes;

        ps.project_supplier_quotes.forEach((psq: any) => {
          const { demand_supplier_id } = psq;

          psq.project_supplier_id = demand_supplier_id;

          // 兼容非PO且以前填写过评分的老单子
          if (ps.overall_score && !psq.score) {
            psq.score = ps.overall_score;
          }

          // 模型测只使用score
          if (_.isNumber(psq.precise_score)) {
            psq.score = psq.precise_score;
          }

          if (psq.currency) {
            psq.currency = psq.currency.toLowerCase();
          }
        });
        // 模型对齐保留新模型数据
        // if (!demandDetail?.structured) {
        //   ps.demand_supplier_quotes = undefined;
        // }
      }
    });
  }

  if (typeof demandDetail.budget === 'string') {
    demandDetail.budget = parseFloat(demandDetail.budget.replace(/,/g, ''));
  }

  // 部分字段转小写， 需要和option返回value对齐；
  function keyToLowerCase(demandDetail: any) {
    if (!demandDetail) return;
    const properties = [
      'budget_currency', // 预算货币
      'group_type', // 组类型
      'oa_status', // OA状态
      'priority', // 优先级
      'state', // 状态
      'quote_currency', // 报价货币
      'settle_currency', // 结算货币
      'support_type', // 支持类型
      'purchasing_method', // 采购方式
      'purchasing_strategy', // 采购策略
    ];
    properties.forEach((property) => {
      if (demandDetail[property]) {
        demandDetail[property] = demandDetail[property]?.toLowerCase?.();
      }
    });
  }

  let keysTransformKeys = {
    background: 'root_demand_ext_data.background',
    expectation: 'root_demand_ext_data.expectation',
  };

  Object.keys(keysTransformKeys).forEach((n) => {
    demandDetail[n] = _.get(demandDetail, keysTransformKeys[n]);
  });

  keyToLowerCase(demandDetail);
  keyToLowerCase(demandDetail['root_demand_ext_data']);

  // 流程节点版本
  demandDetail.$flow_vision = (() => {
    let flowVision = 'v0';
    if (demandDetail.flow_flag === 'OLD_ONLINE_PROCESS') {
      flowVision = 'v0';
    } else if (demandDetail.flow_flag === undefined) {
      flowVision = 'v0';
    } else {
      flowVision = 'v1';
    }
    // mock 测试数据
    if (localStorage.getItem('$flow_vision')) {
      flowVision = localStorage.getItem('$flow_vision');
    }
    return flowVision;
  })();

  // 如果是三级单，则合作通过通过报价信息进行过滤
  if (demandDetail?.depth === 2) {
    let demand_supplier_quotes =
      demandDetail?.demand_suppliers?.[0]?.demand_supplier_quotes;
    if (demand_supplier_quotes?.length > 0) {
      demandDetail.demand_suppliers[0].demand_supplier_quotes =
        demand_supplier_quotes.filter((item: any) => item.winning);
      demand_supplier_quotes =
        demandDetail.demand_suppliers[0].demand_supplier_quotes;
    }
    if (
      demand_supplier_quotes?.length > 0 &&
      demandDetail.demand_plan_info?.cooperate_accounts?.length
    ) {
      demandDetail.demand_plan_info.cooperate_accounts =
        demandDetail.demand_plan_info.cooperate_accounts
          .slice()
          .sort((a: any, b: any) => (a.index_id ?? Number.MAX_SAFE_INTEGER) - (b.index_id ?? Number.MAX_SAFE_INTEGER))
          .map((item: any, index: number) => {
            const it = demand_supplier_quotes?.find?.((v) => {
              return v.cooperate_account_id === item.id;
            });

            let $supplier_settle_price;
            if (demandDetail?.need_cooperate_account_supplier_settle_price) {
              // 合作内容中添加供应商填写的最终结算金额
              $supplier_settle_price = {
                value: it?.supplier_settle_price,
                currency: it?.currency,
              };
            }
            return {
              ...item,
              $orderIndex: _.isNil(item.index_id) ? index : item.index_id - 1,
              $supplier_settle_price,
              case_close_report_cooperate_account_attachments: it?.case_close_report_cooperate_account_attachments, // 结案报告 (差旅、餐补)
              contain_travel_expenses_reimbursement: it?.contain_travel_expenses_reimbursement, // 是否包含差旅费报销
              need_actual_execution: it?.need_actual_execution, // 是否实际执行
            };
          })
          .filter((item: any) => {
            return demand_supplier_quotes.some(
              (quote: any) => quote.cooperate_account_id === item.id
            );
          });
    }
  }

  // 判断 广告投放流程 按消耗结算逻辑
  // 新流程 通过 is_bind_execution_data 判断
  demandDetail.$isAdsXiaohao = (() => {
    switch (demandDetail.$flow_vision) {
      case 'v0':
        return (
          demandDetail?.project_plan_info?.ad_settle_way ===
          QuotationsType.XIAOHAO
        );
      case 'v1':
      default:
        return demandDetail.is_bind_execution_data === 1;
    }
  })();
  if (demandDetail.$isAdsXiaohao) {
    // 兼容旧流程，判断逻辑
    if (typeof demandDetail.project_plan_info === 'object') {
      demandDetail.project_plan_info.ad_settle_way = QuotationsType.XIAOHAO;
    }

    // 绑定执行数据且需求类型为效果类
    if ([19].includes(demandDetail?.l1_category_id)) {
      demandDetail.$bindEffectBuyAmount = true;
    }
    // 绑定执行数据且需求类型为A+
    if ([27].includes(demandDetail?.l1_category_id)) {
      // 绑定执行数据且需求类型为A+
      demandDetail.$bindAPlus = true;
    }
  }

  // 结构化版本大于等于1，则必为合并上传
  if (demandDetail.structured_version >= 1) {
    demandDetail.quote_upload_method = 'MULTI_UPLOAD';
  }

   // 二级单创建时间为2025年2月17日之后且必须上传作品为是
   demandDetail.$contentStructured = !!demandDetail?.content_marketing_case_structured_version 
   && demandDetail?.need_content_marketing_attach;

  /** 喇叭体系-KOL合作 */
  demandDetail.$isHornSystemAndKOL =
    demandDetail.l1_category_id === 22 && demandDetail.l2_category_id === 2210;

  /** 三级单 */
  demandDetail.$thirdLevelOrder = +demandDetail?.depth === 2;
  
  demandDetail.$getTime = new Date().getTime();

  // 一询单数据调整
  if (demandDetail.demand_source_detail_vo) {
    // 取第一个报价币种,小写
    demandDetail.demand_source_detail_vo.$currency = demandDetail.demand_source_detail_vo?.demand_source_supplier_kol_vos?.[0]?.quote_price_currency?.toLowerCase();

    if (demandDetail.demand_source_detail_vo.demand_source_content_vo) {
      demandDetail.demand_source_detail_vo.demand_source_content_vo = demandDetail.demand_source_detail_vo.demand_source_content_vo?.map(v => ({
        ...v,
        $release_time: (v?.begin_at && v?.end_at) ? [dayjs(v?.begin_at).format('YYYY-MM-DD'), dayjs(v?.end_at).format('YYYY-MM-DD')] : undefined,
      }))
    }

    // 达人筛选维度及权重为空时，使用默认值
    if (!demandDetail.demand_source_detail_vo?.demand_source_info?.plan_kol_recommend_weights?.length) {
      _.set(demandDetail, 'demand_source_detail_vo.demand_source_info.plan_kol_recommend_weights', DEFAULT_DIMENSION_WEIGHTS);
    }
  }

  // 对齐 KPI调整数据模型
  // if (demandDetail?.root_demand_ext_data?.expectation_kpi_indicators?.length) {
  //   demandDetail.root_demand_ext_data.expectation_kpi_indicators = demandDetail.root_demand_ext_data.expectation_kpi_indicators.map(v => {
  //     let isNumber = v?.kpi_schema?.type === 'numeric';
  //     return {
  //       ...v,
  //       expect_value: isNumber && v?.expect_value ? +v?.expect_value : v?.expect_value,
  //       actual_value: isNumber && v?.actual_value ? +v?.actual_value : v?.actual_value,
  //     };
  //   });
  // }
  // if (demandDetail?.expectation_kpi_indicators?.length) {
  //   demandDetail.expectation_kpi_indicators = demandDetail.expectation_kpi_indicators.map(v => {
  //     let isNumber = v?.kpi_schema?.type === 'numeric';
  //     return {
  //       ...v,
  //       expect_value: isNumber && v?.expect_value ? +v?.expect_value : v?.expect_value,
  //       actual_value: isNumber && v?.actual_value ? +v?.actual_value : v?.actual_value,
  //     };
  //   });
  // }

  // 对齐 PO结构化数据模型
  alignAtStructured(demandDetail);
};

// ============================================================
// 供应商选择（AddModals/AddSupplierModal 通用函数）
// 从 mpc-inner featrures/MPCDemand/utils.ts 提取
// ============================================================

import { treeOptionsFilter } from '#utils/tree';

/**
 * 根据采购方式获取可选供应商类型
 * 对齐 enums.ChannelSupplierTypeEnum
 */
export const getSelectSupplierType = (purchasing_method?: string): number[] => {
  const typeMap: Record<string, number[]> = {
    s_directional_bidding: [6, 1, 2, 3], // 指定供应商
    s_special_matter_bidding: [6, 1, 2, 3], // 特殊事项报备
    s_only_one_source: [6, 1, 2, 3], // 单一来源
    s_framework: [6, 1, 2, 3], // 引用框架协议
    s_immediately_bidding: [6, 1, 2, 3], // 直接采购
    s_bidding_finished: [6, 1, 2, 3], // 已比选
    s_framework_bidding_finished: [6, 1, 2, 3], // 引用框架协议(已比选)
  };
  return typeMap[purchasing_method || ''] || [6, 1, 2, 3];
};

/**
 * 筛选供应商列表，根据采购方式
 * 供应商等级对齐 ChannelSupplierTypeEnum
 */
export function filterSupplierList(
  supplierList: any[],
  detail: {
    purchasing_method: string;
    purchasing_strategy: string;
  },
) {
  let supplierOptions = _.cloneDeep(supplierList);
  supplierOptions = treeOptionsFilter(supplierOptions, (i: any) => {
    // 没有采购类型代表，比选单
    if (!detail.purchasing_method) {
      return getSelectSupplierType().includes(i.type);
    }

    // 没有渠道类型的，代表新建需求时
    // 根据采购方式筛选供应商
    const purchasing_method = detail.purchasing_method.toLowerCase();

    if (detail?.purchasing_strategy?.toLowerCase?.() === 'multi_quota_finished') {
      return i?.multi_quote_winning_supplier_flag && getSelectSupplierType(purchasing_method).includes(i.type);
    }

    switch (purchasing_method) {
      case 's_only_one_source': // 单一来源，筛选供应商
        return getSelectSupplierType(purchasing_method).includes(i.type) && !!i.single_source;
      case 's_bidding_finished': // 已比选
      case 's_framework_bidding_finished': // 引用框架协议(已比选)
        return i?.relate_biz_type === 'BIDDING' && getSelectSupplierType(purchasing_method).includes(i.type);
      case 's_directional_bidding': // 指定供应商
      case 's_special_matter_bidding': // 特殊事项报备
      case 's_framework': // 引用框架协议
      case 's_immediately_bidding': // 直接采购
      default:
        return getSelectSupplierType(purchasing_method).includes(i.type);
    }
  });

  // 供应商等级排序，将 relate_biz_type === 'BIDDING' 排在前面
  supplierOptions = supplierOptions.map((item: any) => {
    item.title = item.name || item.title;
    if (item.children) {
      item.children = item.children.sort((a: any, b: any) => {
        if (a?.multi_quote_winning_supplier_flag || b?.multi_quote_winning_supplier_flag) {
          return a?.multi_quote_winning_supplier_flag ? -1 : 1;
        }
        if (a?.relate_biz_type === 'BIDDING') {
          return -1;
        }
        // 分数大排在前面
        if (a.recent_overall_score || b.recent_overall_score) {
          return (
            (Number.parseFloat(b.recent_overall_score) || 0) -
            (Number.parseFloat(a.recent_overall_score) || 0)
          );
        }
        return 1;
      });
      item.children.forEach((child: any) => {
        if (!child.label) child.label = child.name;
        child.title = child.name;
        child.key = child.value;
      });
    }
    return item;
  });
  return supplierOptions.filter((n) => !!n?.children?.length);
};
